// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { ApiOpportunityConfigurationResponse } from "../../../src/WorkflowsStore/Opportunity/OpportunityConfigurations/interfaces";

export const mockApiOpportunityConfigurationResponse = {
  enabledConfigs: {
    usMiAddInPersonSecurityClassificationCommitteeReviewV2: {
      callToAction:
        "Complete SCC review and fill out 283 Form for eligible residents, inclusive of ADD signature.",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "PENDING MISCONDUCT", text: "Pending misconduct hearing" },
        {
          key: "NOT IN SEG",
          text: "Resident is not currently in segregation (please ensure this is reflected in OMNI)",
        },
        {
          key: "START DATE INCORRECT",
          text: "Segregation start date is incorrect",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "[2026 Policy] ADD In-Person Review",
      dynamicEligibilityText:
        "resident[|s] [is|are] eligible for in-person review by the ADD at SCC to potentially return to general population [2026 Policy]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usMiOneYearSinceLastAddInPersonSccReview",
          text: "{{record.metadata.daysInSolitarySession}} consecutive days in {{record.metadata.solitarySessionType}};{{#if latestADDInPersonSccReviewDate}} last ADD in-person review recorded on {{date latestADDInPersonSccReviewDate}};{{/if}} ADD in-person review due on or before {{date nextSccDueDate}}",
          tooltip:
            "ADDs shall personally interview each prisoner in their respective regions who has been confined in administrative segregation for twelve continuous months. If the prisoner continues in administrative segregation beyond the first twelve month period, the ADD shall interview the prisoner every twelve months thereafter until the prisoner is released from administrative segregation.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection:
        "US_MI-addInPersonSecurityClassificationCommitteeReviewV2",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 3,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "Incarceration",
        "UsMiRestrictiveHousing",
        "CaseNotes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 30 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usMiOneYearSinceLastAddInPersonSccReview",
          text: "Next ADD in-person review due on or before {{date nextSccDueDate}}",
          tooltip:
            "ADDs shall personally interview each prisoner in their respective regions who has been confined in administrative segregation for twelve continuous months. If the prisoner continues in administrative segregation beyond the first twelve month period, the ADD shall interview the prisoner every twelve months thereafter until the prisoner is released from administrative segregation.",
        },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This page helps staff identify residents in restrictive housing who have spent 1+ consecutive year(s) in segregation and are therefore upcoming or due for an in-person SCC review with the relevant ADD.  Per the policy #04.05.120, people in Administrative Segregation must be reviewed in person by ADD after one year in Administrative Segregation and every year thereafter. Schedule eligible residents for an SCC review and prepare the pre-filled 283 Form, inclusive of ADD signature. Where possible, work to transfer residents who no longer need to be in temporary or administrative segregation back to general population.",
      submittedTabTitle: "SCC Scheduled",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: [
        {
          key: "ELIGIBILITY STATUS",
          tabs: ["Overdue", "Due", "Marked Ineligible", "SCC Scheduled"],
        },
      ],
      tabPrefaceCopy: [
        {
          tab: "Overdue",
          text: "This tab shows people who are Overdue for an ADD review. This means they have been in Administrative Segregation for over 12 months without an ADD review, or it has been 12 months since their last ADD review.",
        },
        {
          tab: "Due",
          text: "This tab shows people who are Upcoming or Due for an ADD review. This means they have been in Administrative Segregation for over 10 months or it has been 10 months since their last ADD review and are approaching 12 months in continuous Administrative Segregation.",
        },
        {
          tab: "Marked Ineligible",
          text: "These residents have been manually reviewed by a user and determined ineligible for an ADD review at the calculated time.",
        },
        {
          tab: "Pending",
          text: "These residents are pending the completion of an ADD review.",
        },
      ],
      tooltipEligibilityText: null,
      urlSection: "addInPersonSecurityClassificationCommitteeReviewV2",
      zeroGrantsTooltip: null,
    },
    usMiClassificationReview: {
      callToAction:
        "Review clients who meet the time threshold for classification review and downgrade supervision levels in COMS.",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "VIOLATIONS",
          text: "Excessive violation behavior during current review period",
        },
        {
          key: "EMPLOYMENT",
          text: "Chronic unemployment with no effort to job search or recent, concerning unemployment",
        },
        {
          key: "FINES & FEES",
          text: "No effort to pay fines and fees despite documented ability to pay",
        },
        {
          key: "CASE PLAN",
          text: "No progress toward completion of Transition Accountability Plan goals/tasks",
        },
        {
          key: "NONCOMPLIANT",
          text: "Noncompliant with the order of supervision",
        },
        { key: "ABSCONSION", text: "Chronic missing of reporting dates" },
        {
          key: "SPECIALTY COURT PARTICIPATION",
          text: "Client is enrolled in a specialty court that prohibits supervision at a lower level.",
        },
        { key: "Other", text: "Other: please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Supervision Level Downgrade upon Classification Review",
      dynamicEligibilityText:
        "client[|s] may be eligible for a supervision level downgrade",
      eligibilityDateText: "Next Classification Due Date",
      eligibleCriteriaCopy: [
        {
          key: "usMiClassificationReviewPastDueDate",
          text: "Recommended classification review date is {{date eligibleDate}}, based on supervision start date and last classification review date",
          tooltip:
            "Classification reviews shall be completed after six months of active supervision […] Subsequent classification reviews shall be scheduled at six-month intervals.",
        },
        {
          key: "usMiPastInitialClassificationReviewDate",
          text: "Recommended classification review date is {{date eligibleDate}}, based on supervision start date and last classification review date",
          tooltip:
            "Classification reviews shall be completed after six months of active supervision […] Subsequent classification reviews shall be scheduled at six-month intervals.",
        },
        {
          key: "usMiSixMonthsPastLastClassificationReviewDate",
          text: "Recommended classification review date is {{date eligibleDate}}, based on supervision start date and last classification review date",
          tooltip:
            "Classification reviews shall be completed after six months of active supervision […] Subsequent classification reviews shall be scheduled at six-month intervals.",
        },
        {
          key: "usMiNotAlreadyOnLowestEligibleSupervisionLevel",
          text: "Currently eligible based on offense type and supervision level",
          tooltip:
            "The supervising Agent shall ensure that a Correctional Offender Management Profiling for Alternative Sanctions (COMPAS) has been completed for each offender on their active caseload as outlined in OP 06.01.145 “Administration and Use of COMPAS and TAP.” Unless mandated by statute or other criteria as directed in this operating procedure, the COMPAS shall be used to determine the initial supervision level of each offender.\nUnless an offender’s supervision level is mandated by policy or statute, the supervising Agent shall reduce an offender’s supervision level if the offender has satisfactorily completed six continuous months at the current assigned supervision level.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_MI-classificationReviewReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 1,
      ineligibleCriteriaCopy: [
        {
          key: "usMiPastInitialClassificationReviewDate",
          text: "Recommended classification review date is {{date eligibleDate}}, based on supervision start date and last classification review date",
          tooltip:
            "Classification reviews shall be completed after six months of active supervision […] Subsequent classification reviews shall be scheduled at six-month intervals.",
        },
        {
          key: "usMiSixMonthsPastLastClassificationReviewDate",
          text: "Recommended classification review date is {{date eligibleDate}}, based on supervision start date and last classification review date",
          tooltip:
            "Classification reviews shall be completed after six months of active supervision […] Subsequent classification reviews shall be scheduled at six-month intervals.",
        },
      ],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "UsMiRecommendedSupervisionLevel",
        "EligibilityDate",
        "ClientProfileDetails",
        "CaseNotes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 180, maxSnoozeDays: 180 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify clients who may be eligible for a supervision level downgrade when a classification review is due or overdue.\n\nA classification review is generally mandated after six months of supervision and at six-month intervals thereafter, though some clients must receive a classification review earlier than six months by policy. Agents may reconsider the supervision level for a client based on developments in their case and behavior; per FOA Field Memorandum 2023-211, agents are presumptively required to downgrade clients’ supervision level during each classification review, provided that they have “satisfactorily completed” the prior six months on supervision.\n\nReview clients who meet the supervision level downgrade criteria and the classification review time threshold as per OP 06.04.130I, and downgrade their supervision level in COMS.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "classificationReview",
      zeroGrantsTooltip: null,
    },
    usMiCustodyLevelDowngrade: {
      callToAction: null,
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "PROGRAMMING",
          text: "The resident is currently participating in programming, e.g. START, that is not available at other facilities or housing units with a lower security level.",
        },
        {
          key: "INVESTIGATION",
          text: "The resident has a pending misconduct investigation.",
        },
        {
          key: "ACCOMMODATION",
          text: "There is a lack of accommodations that the resident requires for religious, medical or other reasons at other facilities or housing units with a lower security level.",
        },
        { key: "OTHER", text: "Other: Please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: "",
      displayName: "Custody Level Reduction",
      dynamicEligibilityText:
        "resident[|s] may be eligible for custody level reduction",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usMiNoClassIOrIiMisconductInSixMonthsAndNoSecurityAssessment",
          text: "6-months misconduct-free since {{date sixMonthMisconductFreeDate}} but has not been assessed since {{date mostRecentAssessmentDate}}",
          tooltip: "",
        },
        {
          key: "usMiManagementLevelWithinSixPointsOfLowerLevel",
          text: "Management level could be reduced due to favorable behavior",
          tooltip:
            "Management score is {{managementLevelRawScore}}. A 6-point (5-point for female facility) reduction to management score due to favorable behavior would result in a lower management level.",
        },
        {
          key: "usMiManagementLevelGreaterThanConfinementLevel",
          text: "Management level is currently determining true security level",
          tooltip:
            "True security level is determined by the higher level between management level and confinement level. This resident has a management level of {{managementLevel}}, above their confinement level of {{confinementLevel}}. A reduction to their management level could result in an overall reduction to their  true security level. ",
        },
        {
          key: "usMiActualPlacementLevelLessThanCustodyLevel",
          text: "Actual Security Level {{actualPlacementLevel}} on most recent assessment is lower than current Security Level",
          tooltip:
            "Most recent assessment was done on {{date mostRecentAssessmentDate}}",
        },
        {
          key: "usMiManagementLevelCouldDropToLowerLevel",
          text: "Management level could be reduced due to favorable behavior",
          tooltip:
            "Management score is {{managementLevelRawScore}}. A 6-point (5-point for female facility) reduction to management score due to favorable behavior would result in a lower management level.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_MI-custodyLevelDowngrade",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 4,
      ineligibleCriteriaCopy: [
        {
          key: "usMiNoClassIOrIiMisconductInSixMonthsAndNoSecurityAssessment",
          text: "Will be 6-months misconduct-free on {{date sixMonthMisconductFreeDate}} and has not been assessed since {{date mostRecentAssessmentDate}}",
          tooltip:
            "Last assessment was completed on {{date mostRecentAssessmentDate}} and resident will be 6-month misconduct-free on {{date sixMonthMisconductFreeDate}}.",
        },
      ],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [
        {
          tab: "Needs Re-Screen",
          texts: ["RE-SCREEN_PENDING_BED_SPACE", "RE-SCREEN_IN_PROGRESS"],
        },
        {
          tab: "Needs Review",
          texts: ["RE-SCREEN_PENDING_BED_SPACE", "RE-SCREEN_IN_PROGRESS"],
        },
        {
          tab: "Marked Ineligible",
          texts: ["RE-SCREEN_PENDING_BED_SPACE", "RE-SCREEN_IN_PROGRESS"],
        },
      ],
      methodologyUrl: "tbd",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: "Validated by data from COMS & OMNI",
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["UsMiLastAssessment", "Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 180 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [
        { subcategory: "ELIGIBLE_FOR_REVIEW", text: "Eligible for Review" },
        {
          subcategory: "ALMOST_ELIGIBLE_FOR_REVIEW",
          text: "Almost Eligible for Review",
        },
        {
          subcategory: "RE-SCREEN_PENDING_BED_SPACE",
          text: "Re-Screen Pending Bed Space",
        },
        { subcategory: "RE-SCREEN_IN_PROGRESS", text: "Re-Screen in Progress" },
      ],
      subcategoryOrderings: [
        {
          tab: "Needs Review",
          texts: ["ELIGIBLE_FOR_REVIEW", "ALMOST_ELIGIBLE_FOR_REVIEW"],
        },
        {
          tab: "Needs Re-Screen",
          texts: ["RE-SCREEN_PENDING_BED_SPACE", "RE-SCREEN_IN_PROGRESS"],
        },
      ],
      subheading:
        "This alert helps staff identify residents who may be newly eligible for a custody level reduction because they have not had a Class I or II misconduct in over 6 months. ",
      submittedTabTitle: "Needs Re-Screen",
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: [
        {
          key: "ELIGIBILITY STATUS",
          tabs: [
            "Needs Review",
            "Needs Re-Screen",
            "Transfer in Progress",
            "Marked Ineligible",
          ],
        },
      ],
      tabPrefaceCopy: [
        {
          tab: "Needs Review",
          text: "Residents qualify as eligible for a re-screen and reduction to a lower custody level after six months of misconduct-free behavior. Residents are considered almost eligible if they remain misconduct-free and are within one month of reaching this six-month milestone.",
        },
        {
          tab: "Marked Ineligible",
          text: "These residents were not deemed eligible for a custody reduction at this time.",
        },
        {
          tab: "Eligible for Review",
          text: "Residents who may be eligible for a re-screen and reduction to a lower custody level because they have not had any misconducts in 6 months or more. They can be reviewed to confirm eligibility.",
        },
        {
          tab: "Almost Eligible for Review",
          text: "Residents who may be almost eligible for a re-screen and reduction to a lower custody level if they remain misconduct-free for up to one more month",
        },
        {
          tab: "Needs Re-Screen",
          text: "The Re-screen Pending Bed Space section shows residents who have been identified as eligible for a re-screen to be reduced, but there is no bed space currently available at the lower level. This can be used to track who could be downgraded when bed space becomes available. The Re-Screen in Progress section shows residents who have been identified as immediately eligible for a re-screen so they can be transferred to a lower custody level. This can be used to track which re-screens can be completed immediately. ",
        },
        {
          tab: "Transfer in Progress",
          text: "Residents who have a lower Actual Security Level on their latest security screen than their current custody level, and who are actively transferring to a lower custody level.",
        },
      ],
      tooltipEligibilityText: null,
      urlSection: "custodyLevelDowngrade",
      zeroGrantsTooltip: null,
    },
    usMiEarlyDischarge: {
      callToAction:
        "Review clients who may be eligible for early discharge and complete discharge paperwork in COMS.",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "CHILD ABUSE ORDER",
          text: "Child abuse prevention order filed during supervision period",
        },
        {
          key: "SUSPECTED OFFENSE",
          text: "Suspected of a felony, assaultive misdemeanor, OWI, or offense requiring SORA registration",
        },
        {
          key: "FELONY/STATE PROBATION",
          text: "On parole and also on other state or federal probation supervision for an offense committed during the current period",
        },
        {
          key: "NEEDS",
          text: "On parole and all criminogenic needs have not been addressed",
        },
        {
          key: "NONCOMPLIANT",
          text: "Not compliant with the order of supervision",
        },
        {
          key: "PROGRAMMING",
          text: "Has not completed all required programming including specialty court conditions",
        },
        { key: "PRO-SOCIAL", text: "Has not demonstrated pro-social behavior" },
        {
          key: "RESTITUTION",
          text: "Has not completed court-ordered restitution payments",
        },
        {
          key: "FINES & FEES",
          text: "Willful nonpayment of restitution, fees, court costs, fines, and other monetary obligations despite clear ability to pay",
        },
        { key: "PENDING CHARGES", text: "Pending felony charges/warrant" },
        {
          key: "ORDERED TREATMENT",
          text: "Has not completed all required treatment",
        },
        {
          key: "EXCLUDED OFFENSE",
          text: "On parole for an offense resulting in death or serious bodily injury or an offense involving the discharge of a firearm",
        },
        {
          key: "EXCLUDED CURRENT OFFENSE",
          text: "On probation for MCL 750.81 or MCL 750.84, for an offense requiring a mandatory probation term (750.411H, 750.411I, 750.136b, 400.1501), or for a sex offense",
        },
        {
          key: "EXCLUDED NEW OFFENSE",
          text: "A new offense while on probation that is a felony, assaultive misdemeanor, or requires SORA registration",
        },
        {
          key: "JUDGE",
          text: "County Judge declined client for consideration",
        },
        { key: "Other", text: "Other: please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Early Discharge",
      dynamicEligibilityText: "client[|s] may be eligible for early discharge",
      eligibilityDateText: "Earliest Eligibility Date for Early Discharge",
      eligibleCriteriaCopy: [
        {
          key: "supervisionOrSupervisionOutOfStatePastHalfFullTermReleaseDate",
          text: 'Completed at least half of {{#if (eq record.metadata.supervisionType "Parole")}}parole{{else}}probation{{/if}} term',
          tooltip:
            '{{#if (eq record.metadata.supervisionType "Parole")}}A parolee is eligible for early discharge consideration prior to the expiration of the original term of parole if they have completed at least one-half of an original parole term of 12 months or more{{else}}An offender may be considered for discharge prior to the expiration of the original term of probation if they have completed at least one-half of the probation term{{/if}}',
        },
        {
          key: "servingAtLeastOneYearOnParoleSupervisionOrSupervisionOutOfState",
          text: "Serving a parole term of 12 months or more",
          tooltip:
            "A parolee is eligible for early discharge consideration prior to the expiration of the original term of parole if they have completed at least one-half of an original parole term of 12 months or more",
        },
        {
          key: "usMiParoleDualSupervisionPastEarlyDischargeDate",
          text: "Served mandatory period of parole",
          tooltip:
            "The parolee has served any mandatory period of parole as set forth in Paragraph F. ",
        },
        {
          key: "usMiNoActivePpo",
          text: 'No active PPO ordered during the {{#if (eq record.metadata.supervisionType "Parole")}}parole{{else}}probation{{/if}} term',
          tooltip:
            'The {{#if (eq record.metadata.supervisionType "Parole")}}parolee{{else}}offender{{/if}} does not have an active PPO […] that was ordered against him/her during the {{#if (eq record.metadata.supervisionType "Parole")}}parole{{else}}probation{{/if}} term.',
        },
        {
          key: "usMiNoNewIneligibleOffensesForEarlyDischargeFromSupervision",
          text: 'Not convicted of a felony, assaultive misdemeanor, or offense requiring SORA registration while on {{#if (eq record.metadata.supervisionType "Parole")}}parole{{else}}probation{{/if}}',
          tooltip:
            '{{#if (eq record.metadata.supervisionType "Parole")}}The parolee is not known to have been involved in […] felonious behavior, assaultive misdemeanor behavior (as set forth in Attachment A) […] or an offense that requires registration under the Sex Offender Registration Act while on parole.{{else}}The offender is not known to have been involved in […] felonious behavior or assaultive misdemeanor behavior as set forth in Attachment A “OP 06.01.145B Assaultive Misdemeanor List” which occurred while on probation or any offense that requires registration under the Sex Offender Registration Act (SORA), which occurred while on probation.{{/if}}',
        },
        {
          key: "usMiNotServingIneligibleOffensesForEarlyDischargeFromParoleDualSupervision",
          text: "Not serving for an offense excluded from early discharge eligibility by policy",
          tooltip:
            "The parolee is not serving for an offense […] required to be registered under the Sex Offender Registration Act.",
        },
        {
          key: "usMiNotServingIneligibleOffensesForEarlyDischargeFromProbationSupervision",
          text: "Not serving for an offense excluded from early discharge eligibility by policy",
          tooltip:
            "The offender is not currently serving for an offense that requires a mandatory term of probation as identified in Paragraph H. The offender is not currently serving for MCL 750.81 or MCL 750.84 (Assault with Intent to commit Great Bodily Harm Less than Murder).",
        },
        {
          key: "usMiSupervisionOrSupervisionOutOfStateLevelIsNotSai",
          text: "Not paroled from SAI on current term",
          tooltip:
            "The parolee was not paroled from the Special Alternative Incarceration (SAI) program on the current term (see definition).",
        },
        {
          key: "supervisionOrSupervisionOutOfStateLevelIsNotHigh",
          text: "Not on intensive supervision",
        },
        {
          key: "usMiNoOwiViolationOnParoleDualSupervision",
          text: "Not convicted of an OWI offense while on parole",
          tooltip:
            "The parolee is not known to have been involved in […] a violation of MCL 257.625 (OWI) […] while on parole.",
        },
        { key: "usMiNoPendingDetainer", text: "No pending detainers" },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_MI-earlyDischargeReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 2,
      ineligibleCriteriaCopy: [
        {
          key: "supervisionOrSupervisionOutOfStatePastHalfFullTermReleaseDate",
          text: "Needs {{monthsOrDaysRemainingFromToday eligibleDate}} on supervision",
          tooltip:
            '{{#if (eq record.metadata.supervisionType "Parole")}}A parolee is eligible for early discharge consideration prior to the expiration of the original term of parole if they have completed at least one-half of an original parole term of 12 months or more{{else}}An offender may be considered for discharge prior to the expiration of the original term of probation if they have completed at least one-half of the probation term{{/if}}',
        },
        {
          key: "usMiParoleDualSupervisionPastEarlyDischargeDate",
          text: "Needs {{monthsOrDaysRemainingFromToday eligibleDate}} on supervision",
          tooltip:
            "A parolee is eligible for early discharge consideration once they have served any mandatory period of parole as set forth in Paragraph F. ",
        },
      ],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
      nonOmsCriteria: [
        { text: "Must have completed all required programming and treatment" },
        {
          text: "Must have completed court-ordered restitution payments",
          tooltip:
            "The client has not willfully failed to pay restitution or crime victim assessment(s)",
        },
        {
          text: "Must have paid or made a good faith effort to pay restitution, fees, court costs, fines, and other monetary obligations",
          tooltip:
            "The client has not willfully failed to pay monetary obligations. Unless another standard is prescribed by the court, the Agent shall not regard the offender's failure to pay as willful if any of the following apply: \n1. The client provides documentation of disability and has little or no income. \n2. The client is on public assistance. \n3. The client has been unemployed for the majority of the probation term despite earnest job-seeking efforts and has little or no income from any other source. \n4. The client annual income is at or below the federal poverty guidelines (refer to the information at https://aspe.hhs.gov). \n5. The client net income is above federal poverty guidelines but has been and remains insufficient to satisfy the terms of the obligations ordered. \n6. The client has made earnest efforts to fulfill payment obligations, but the amount owed is such that complete payment within the scheduled probation term is unrealistic.",
        },
        {
          text: 'Must have no pending felony charges or warrants ({{#if (eq record.metadata.supervisionType "Parole")}}including any involvement or suspicion of felonious behavior, assaultive misdemeanor behavior, violation of OWI, or any offense that requires registration under SORA which occurred while on parole{{else}}including any involvement or suspicion of felonious behavior, assaultive misdemeanor behavior, or any offense that requires registration under SORA which occurred while on probation{{/if}})',
        },
      ],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "ActionHistory",
        "UsMiEarlyDischargeIcDetails",
        "ClientProfileDetails",
        "EligibilityDate",
        "UsMiSecondaryOfficersAndDockets",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "Early Discharge is the termination of the period of probation or parole before the full-term discharge date. Early discharge reviews are mandated, at minimum, once clients have served half of their original term of supervision.<br /><br />Review clients who may be eligible for early discharge as per OP 06.05.135 and OP 06.04.130H and complete the discharge paperwork in COMS.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "earlyDischarge",
      zeroGrantsTooltip: null,
    },
    usMiInitialClassificationReview2026Policy: {
      callToAction: "Calculate Initial Classification",
      caseNotesTitle: "Relevant Information For Classification",
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [],
      denialText: null,
      deniedTabTitle: null,
      displayName: "[2026 Policy] Initial Classification",
      dynamicEligibilityText:
        "resident[|s] may need an Initial Classification [2026 Policy]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "notHasInitialClassificationInStatePrisonCustody",
          text: "Has not had initial classification since latest start in MDOC custody",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_MI-initialClassificationReview2026Policy",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 7,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl: "https://example.com",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: ["Incarceration", "CaseNotes"],
      skipFormPreview: false,
      snooze: null,
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading: null,
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "initialClassificationReview2026Policy",
      zeroGrantsTooltip: null,
    },
    usMiMinimumTelephoneReporting: {
      callToAction:
        "Review clients who meet the requirements for minimum telephone reporting and change supervision levels in COMS.",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "FIREARM",
          text: "Serving on a felony offense involving possession or use of a firearm",
        },
        {
          key: "SPEC COURT",
          text: "Enrolled in a special issue court (e.g. Drug Treatment Court, Recovery Court, MH Court, Veterans Court)",
        },
        {
          key: "RPOSN",
          text: "Designated as Reentry Project for Offenders with Special Needs (RPOSN - D-47)",
        },
        {
          key: "HIGH PROFILE",
          text: "Currently serving for an offense that resulted in the death of a person or a high-profile case with adverse community reaction (requires Max or higher based on risk score)",
        },
        {
          key: "JUDGE",
          text: "County Judge declined client for consideration",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Minimum Telephone Reporting",
      dynamicEligibilityText:
        "client[|s] may be eligible for downgrade to a minimum telephone reporting",
      eligibilityDateText:
        "Earliest Eligibility Date for Minimum Telephone Reporting",
      eligibleCriteriaCopy: [
        {
          key: "onMinimumSupervisionAtLeastSixMonths",
          text: "Served at least six months on Minimum In-Person or Minimum Low Risk supervision",
          tooltip:
            "Offenders assigned to minimum in person or minimum low-risk supervision shall be evaluated for assignment to minimum TRS after they have completed six months of active supervision.",
        },
        {
          key: "usMiSupervisionAndAssessmentLevelEligibleForTelephoneReporting",
          text: "Has eligible original COMPAS score of {{titleCase initialAssessmentLevel}}",
          tooltip:
            "Original COMPAS score was minimum or medium and current supervision level is minimum in person or current supervision level is minimum low risk.",
        },
        {
          key: "usMiNotRequiredToRegisterUnderSora",
          text: "Not required to register per SORA",
          tooltip:
            "Not currently required to register pursuant to to the Sex Offender Registration Act.",
        },
        {
          key: "usMiNotServingIneligibleOffensesForTelephoneReporting",
          text: "Not on supervision for an offense excluded from eligibility for telephone reporting",
          tooltip:
            "Not currently serving for an offense listed in WS 01.06.115 Attachment A “Michigan Sex Offender Registry Offenses” or any any similar offense from another state. Not currently serving for an offense included in OP 06.04.130K Attachment A “TRS Exclusion List” including Attempts, Solicitation and Conspiracy. Agents should reference the PACC code on the list when determining eligibility. Not serving for Operating Under the Influence of Liquor (OUIL) or Operating While Impaired (OWI) (any level), unless the offender has successfully completed twelve months of active supervision. A probationer currently serving for OUIL/OWI may only be placed on TRS if authorized by the sentencing court and documented by a court order. Not serving a life or commuted sentence. Not serving a probation term with a delay of sentence.",
        },
        {
          key: "supervisionNotPastFullTermCompletionDateOrUpcoming90Days",
          text: "More than 90 days remaining until full-term discharge.",
        },
        {
          key: "usMiIfServingAnOuilOrOwiHasCompleted12MonthsOnSupervision",
          text: "{{#if eligibleDate}}\nSuccessfully completed twelve months of active supervision if serving for an OUIL or OWI\n{{/if}}",
          tooltip:
            "Offenders serving for an OUIL or OWI shall be evaluated for assignment to minimum TRS after they have completed twelve months of active supervision.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_MI-minimumTelephoneReporting",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 3,
      ineligibleCriteriaCopy: [
        {
          key: "onMinimumSupervisionAtLeastSixMonths",
          text: "Needs {{monthsOrDaysRemainingFromToday eligibleDate}} on minimum in person or minimum low-risk supervision",
          tooltip:
            "Offenders assigned to minimum in person or minimum low-risk supervision shall be evaluated for assignment to minimum TRS after they have completed six months of active supervision.",
        },
        {
          key: "usMiIfServingAnOuilOrOwiHasCompleted12MonthsOnSupervision",
          text: "Needs {{monthsOrDaysRemainingFromToday eligibleDate}} on supervision",
          tooltip:
            "Offenders serving for an OUIL or OWI shall be evaluated for assignment to minimum TRS after they have completed twelve months of active supervision.",
        },
      ],
      initialHeader: null,
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "ClientProfileDetails",
        "EligibilityDate",
        "ActionHistory",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "Minimum Telephone Reporting is a level of supervision that uses an interactive voice recognition system, rather than requiring regular face-to-face contacts.<br /><br />Review clients who meet the requirements for minimum telephone reporting as per OP 06.04.130K and transfer them to telephone reporting in COMS.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "minimumTelephoneReporting",
      zeroGrantsTooltip: null,
    },
    usMiPastFTRD: {
      callToAction:
        "Review clients who are nearing or past their full-term release date and complete discharges in COMS.",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "DATE", text: "Expiration date is inaccurate" },
        { key: "CUSTODY", text: "Client is currently in custody" },
        { key: "Other", text: "Other: please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Full-Term Release",
      dynamicEligibilityText:
        "client[|s] [is|are] nearing or past their full-term release date",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "supervisionTwoDaysPastFullTermCompletionDate",
          text: "{{daysPast eligibleDate}} days past FTRD ({{date eligibleDate}})",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_MI-pastFTRDReferrals",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 4,
      ineligibleCriteriaCopy: [
        {
          key: "supervisionTwoDaysPastFullTermCompletionDate",
          text: "{{daysUntil eligibleDate}} days until FTRD ({{date eligibleDate}})",
        },
      ],
      initialHeader: null,
      isAlert: true,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "ClientProfileDetails",
        "UsMiSecondaryOfficersAndDockets",
      ],
      skipFormPreview: false,
      snooze: {
        autoSnoozeParams: { params: { days: 30 }, type: "snoozeDays" },
      },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify supervision clients who are past their full-term release date and directs staff to complete the discharge in COMS.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: [
        {
          key: "ELIGIBILITY STATUS",
          tabs: ["Overdue", "Upcoming", "Pending", "Overridden"],
        },
      ],
      tabPrefaceCopy: [],
      tooltipEligibilityText: "Eligible for discharge",
      urlSection: "pastFTRD",
      zeroGrantsTooltip: null,
    },
    usMiSecurityClassificationCommitteeReviewV2: {
      callToAction:
        "Complete SCC review and fill out 283 Form for eligible residents",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "PENDING MISCONDUCT", text: "Pending misconduct hearing" },
        {
          key: "NOT IN SEG",
          text: "Resident is not currently in segregation (please ensure this is reflected in OMNI)",
        },
        {
          key: "START DATE INCORRECT",
          text: "Segregation start date is incorrect",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: "Marked Ineligible",
      displayName: "[2026 Policy] Security Classification Committee Review ",
      dynamicEligibilityText:
        "resident[|s] [is|are] eligible for SCC review to potentially return to general population [2026 Policy]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usMiPastAdSegSecurityClassificationCommitteeReviewDate",
          text: "{{record.metadata.daysInSolitarySession}} consecutive days in {{record.metadata.solitarySessionType}};{{#if lastSccReviewDate}} last SCC review recorded on {{date lastSccReviewDate}};{{/if}} SCC review due on or before {{date nextSccDueDate}}",
          tooltip:
            "An SCC review shall be conducted within 14 calendar days of the prisoner being classified to administrative segregation. SCC shall review the prisoner at least every 30 calendar days thereafter until the prisoner is reclassified to general population status.",
        },
        {
          key: "usMiPastTempSegSecurityClassificationCommitteeReviewDate",
          text: "{{record.metadata.daysInSolitarySession}} consecutive days in {{record.metadata.solitarySessionType}};{{#if lastSccReviewDate}} last SCC review recorded on {{date lastSccReviewDate}};{{/if}} SCC review due on or before {{date nextSccDueDate}}",
          tooltip:
            "An SCC review shall be conducted within seven business days of the prisoner being classified to temporary segregation. SCC shall review the prisoner at least every 30 calendar days thereafter until the prisoner is reclassified to general population status.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_MI-securityClassificationCommitteeReviewV2",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 1,
      ineligibleCriteriaCopy: [],
      initialHeader:
        "Complete SCC review and fill out 283 Form for eligible residents.",
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "Incarceration",
        "UsMiRestrictiveHousing",
        "CaseNotes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 30 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usMiPastAdSegSecurityClassificationCommitteeReviewDate",
          text: "Next SCC review due on or before {{date nextSccDueDate}}",
          tooltip:
            "An SCC review shall be conducted within 14 calendar days of the prisoner being classified to administrative segregation. SCC shall review the prisoner at least every 30 calendar days thereafter until the prisoner is reclassified to general population status.\n",
        },
        {
          key: "usMiPastTempSegSecurityClassificationCommitteeReviewDate",
          text: "Next SCC review due on or before {{date nextSccDueDate}}",
          tooltip:
            "An SCC review shall be conducted within seven business days of the prisoner being classified to temporary segregation. SCC shall review the prisoner at least every 30 calendar days thereafter until the prisoner is reclassified to general population status.",
        },
      ],
      subcategoryHeadings: [
        {
          subcategory: "Administrative Segregation",
          text: "Administrative Segregation",
        },
        { subcategory: "Temporary Segregation", text: "Temporary Segregation" },
      ],
      subcategoryOrderings: [
        {
          tab: "Due",
          texts: ["Temporary Segregation", "Administrative Segregation"],
        },
        {
          tab: "Overdue",
          texts: ["Temporary Segregation", "Administrative Segregation"],
        },
        {
          tab: "Not Due",
          texts: ["Temporary Segregation", "Administrative Segregation"],
        },
        {
          tab: "Pending",
          texts: ["Temporary Segregation", "Administrative Segregation"],
        },
        {
          tab: "Marked Ineligible",
          texts: ["Temporary Segregation", "Administrative Segregation"],
        },
        {
          tab: "SCC Scheduled",
          texts: ["Temporary Segregation", "Administrative Segregation"],
        },
      ],
      subheading:
        "This page helps staff identify residents in Temporary Segregation or Administrative Segregation who may be due, overdue, or have an upcoming Security Classification Committee (SCC) Review. Per the policy #04.05.120, people moved to Temporary Segregation must have an SCC within 7 business days of starting segregation, and every 30 calendar days after. People in Administrative Segregation must be reviewed within 14 calendar days and every 30 calendar days thereafter. Schedule eligible residents for an SCC review and prepare the pre-filled 283 Form. Where possible, work to transfer residents who no longer need to be in temporary or administrative segregation back to general population.",
      submittedTabTitle: "SCC Scheduled",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: [
        {
          key: "ELIGIBILITY STATUS",
          tabs: [
            "Overdue",
            "Due",
            "Not Due",
            "Marked Ineligible",
            "SCC Scheduled",
          ],
        },
      ],
      tabPrefaceCopy: [
        {
          tab: "Overdue",
          text: "This tab shows people who are Overdue for an SCC review, based on their segregation type. For people in Temporary Segregation awaiting initial review, they will show up in this tab on and after the 8th business day. For people in Temporary Segregation awaiting a follow-up review, they will show up in this tab 31 calendar days after their last review. For people in Administrative Segregation awaiting initial review, they will show in this tab on and after 15 calendar days. For people in Administrative Segregation awaiting a follow-up review, they will show in this tab 31 calendar days after their last review.",
        },
        {
          tab: "Due",
          text: "This tab shows people who are Upcoming or Due for an SCC review, based on their segregation type.  For people in Temporary Segregation awaiting initial review, they will show up in this tab on business days 1 to 7 after their segregation starts.  For people in Temporary Segregation awaiting a follow-up review, they will show up in this tab on calendar days 24  to 30 after their last review.  For people in Administrative Segregation awaiting initial review, they will show in this tab on calendar days 1 to 14 after their segregation starts.  For people in Administrative Segregation awaiting a follow-up review, they will show in this tab on calendar days 24 to 30 after their last review.",
        },
        {
          tab: "Not Due",
          text: "This tab shows people who are in Administrative or Temporary Segregation, but not currently due for an SCC review. For anyone awaiting a follow up review, they will show up in this tab on calendar day 1 to 23 since their last review.",
        },
        {
          tab: "Marked Ineligible",
          text: "These residents have been manually reviewed by a user and determined ineligible for an SCC review at the calculated time.",
        },
        {
          tab: "Pending",
          text: "These residents have been marked by a user as scheduled for an upcoming SCC review",
        },
      ],
      tooltipEligibilityText: null,
      urlSection: "securityClassificationCommitteeReviewV2",
      zeroGrantsTooltip: null,
    },
    usMiSupervisionLevelDowngrade: {
      callToAction:
        "Review clients whose supervision level does not match their risk level and change supervision levels in COMS.",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        {
          key: "OVERRIDE",
          text: "Agent supervision level override due to noncompliance with supervision",
        },
        {
          key: "EXCLUDED CHARGE",
          text: "Client is required to be supervised at a higher level of supervision by policy",
        },
        {
          key: "SPECIALTY COURT PARTICIPATION",
          text: "Client is enrolled in a specialty court that prohibits supervision at a lower level.",
        },
        {
          key: "ELECTRONIC MONITORING",
          text: "Client is enrolled in electronic monitoring which prohibits supervision at a lower level.",
        },
        { key: "Other", text: "Other: please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "Supervision Level Mismatch",
      dynamicEligibilityText:
        "client[|s] within their first 6 months of supervision [is|are] being supervised at a level that does not match their latest risk score",
      eligibilityDateText: "Initial Classification Due Date",
      eligibleCriteriaCopy: [
        {
          key: "supervisionLevelHigherThanAssessmentLevel",
          text: "Currently supervised at {{supervisionLevel}}; Latest COMPAS score is {{assessmentLevel}}",
          tooltip:
            "The supervising Agent shall ensure that a Correctional Offender Management Profiling for Alternative Sanctions (COMPAS) has been completed for each offender on their active caseload as outlined in OP 06.01.145 “Administration and Use of COMPAS and TAP.”  Unless mandated by statute or other criteria as directed in this operating procedure, the COMPAS shall be used to determine the initial supervision level of each offender.  Any offender placed on active supervision without a completed COMPAS shall be supervised at a Medium level of supervision until a COMPAS can be completed (unless a higher level of supervision is mandated as outlined in this operating procedure).",
        },
        {
          key: "usMiNotPastInitialClassificationReviewDate",
          text: "Initial classification review date is upcoming",
          tooltip:
            "Classification reviews shall be completed after six months of active supervision.  Unless an offender’s supervision level is mandated by policy or statute, the supervising Agent shall reduce an offender’s supervision level if the offender has satisfactorily completed six continuous months at the current assigned supervision level.",
        },
        {
          key: "usMiNotServingIneligibleOffensesForDowngradeFromSupervisionLevel",
          text: "Not serving for an offense ineligible for a lower supervision level",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection: "US_MI-supervisionLevelDowngrade",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 5,
      ineligibleCriteriaCopy: [],
      initialHeader: null,
      isAlert: true,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "ClientProfileDetails",
        "EligibilityDate",
        "CaseNotes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 90 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This alert helps staff identify clients who are eligible for a downgrade in their supervision level during their first six months on supervision. The tool will surface clients who have not yet received a COMPAS but are being supervised at a level other than medium or whose supervision level is not aligned with their COMPAS score.<br /><br />Review clients within their first six months of supervision and whose supervision level does not match their risk level as per OP 06.04.130I and downgrade their supervision level in COMS.",
      submittedTabTitle: null,
      supervisorReviewTabTitle: null,
      supportsIneligible: false,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "SUPERVISION",
      tabGroups: null,
      tabPrefaceCopy: [],
      tooltipEligibilityText: null,
      urlSection: "supervisionLevelMismatch",
      zeroGrantsTooltip: null,
    },
    usMiWardenInPersonSecurityClassificationCommitteeReviewV2: {
      callToAction:
        "resident[|s] [is|are] eligible for in-person review by the Warden at SCC to potentially return to general population",
      caseNotesTitle: null,
      compareBy: null,
      denialAdjective: null,
      denialNoun: null,
      denialReasons: [
        { key: "PENDING MISCONDUCT", text: "Pending misconduct hearing" },
        {
          key: "NOT IN SEG",
          text: "Resident is not currently in segregation (please ensure this is reflected in OMNI)",
        },
        {
          key: "START DATE INCORRECT",
          text: "Segregation start date is incorrect",
        },
        { key: "Other", text: "Other, please specify a reason" },
      ],
      denialText: null,
      deniedTabTitle: null,
      displayName: "[2026 Policy] Warden In-Person Review",
      dynamicEligibilityText:
        "resident[|s] [is|are] eligible for in-person review by the Warden at SCC to potentially return to general population [2026 Policy]",
      eligibilityDateText: null,
      eligibleCriteriaCopy: [
        {
          key: "usMiSixMonthsSinceLastWardenInPersonSccReview",
          text: "{{record.metadata.daysInSolitarySession}} consecutive days in {{ record.metadata.solitarySessionType}};{{#if latestWardenInPersonSccReviewDate}} last Warden in-person review recorded on {{date latestWardenInPersonSccReviewDate}};{{/if}} Warden in-person review due on or before {{date nextSccDueDate}}",
          tooltip:
            "Wardens shall personally interview each prisoner in their respective facilities who has been confined in administrative segregation for six continuous months. If the prisoner continues in administrative segregation beyond the first six month period, the Warden shall interview the prisoner every six months thereafter until the prisoner is released from administrative segregation.",
        },
      ],
      emptyTabCopy: [],
      enabledColumns: [],
      firestoreCollection:
        "US_MI-wardenInPersonSecurityClassificationCommitteeReviewV2",
      grantApprovedStatusMessage: null,
      grantApprovedTabTitle: null,
      grantReviewDropdownLabel: null,
      grantReviewStatusMessage: null,
      hideDenialRevert: false,
      highlightCasesOnHomepage: false,
      highlightedCaseCtaCopy: null,
      homepagePosition: 2,
      ineligibleCriteriaCopy: [],
      initialHeader:
        "Complete SCC review and fill out 283 Form for eligible residents, inclusive of Warden signature.",
      isAlert: false,
      markSubmittedOnFormDownload: true,
      markSubmittedOptionsByTab: [],
      methodologyUrl:
        "https://drive.google.com/file/d/1tdYaic6jvsdTZHZTeGzUVtHL7_SGfyk5/view",
      nonOmsCriteria: [],
      nonOmsCriteriaHeader: null,
      notifications: [],
      omsCriteriaHeader: null,
      overdueOpportunityCalloutCopy: null,
      priority: "NORMAL",
      reasonsRequiringApproval: [],
      reviewerFeatureVariant: null,
      sidebarComponents: [
        "Incarceration",
        "UsMiRestrictiveHousing",
        "CaseNotes",
      ],
      skipFormPreview: false,
      snooze: { defaultSnoozeDays: 30, maxSnoozeDays: 30 },
      snoozeCompanionOpportunityTypes: [],
      snoozeReviewStatusMessage: null,
      stateCode: "US_MI",
      strictlyIneligibleCriteriaCopy: [
        {
          key: "usMiSixMonthsSinceLastWardenInPersonSccReview",
          text: "Next Warden in-person review due on or before {{date nextSccDueDate}}",
          tooltip:
            "Wardens shall personally interview each prisoner in their respective facilities who has been confined in administrative segregation for six continuous months. If the prisoner continues in administrative segregation beyond the first six month period, the Warden shall interview the prisoner every six months thereafter until the prisoner is released from administrative segregation.",
        },
      ],
      subcategoryHeadings: [],
      subcategoryOrderings: [],
      subheading:
        "This page helps staff identify residents in restrictive housing who have spent 6+ consecutive months in segregation and are therefore upcoming or due for an in-person SCC review with the Warden. Per policy #04.05.120, people in Administrative Segregation must be reviewed in person by the Warden after six months in Administrative Segregation and every six months thereafter. Schedule eligible residents for an SCC review and prepare the pre-filled 283 Form inclusive of Warden signature. Where possible, work to transfer residents who no longer need to be in temporary or administrative segregation back to general population.",
      submittedTabTitle: "SCC Scheduled",
      supervisorReviewTabTitle: null,
      supportsIneligible: true,
      supportsSubmitted: true,
      supportsSupervisorReviewOnGrants: false,
      supportsSupervisorReviewOnSnooze: false,
      systemType: "INCARCERATION",
      tabGroups: [
        {
          key: "ELIGIBILITY STATUS",
          tabs: ["Overdue", "Due", "Marked Ineligible", "SCC Scheduled"],
        },
      ],
      tabPrefaceCopy: [
        {
          tab: "Overdue",
          text: "This tab shows people who are Overdue for a Warden review. This means they have been in Administrative Segregation for over 6 months without a Warden review, or it has been 6 months since their last Warden review.",
        },
        {
          tab: "Due",
          text: "This tab shows people who are Upcoming or Due for an Warden review. This means they have been in Administrative Segregation for over 4 months or it has been 4 months since their last Warden review and are approaching 6 months in continuous Administrative Segregation.",
        },
        {
          tab: "Marked Ineligible",
          text: "These residents have been manually reviewed by a user and determined ineligible for a Warden review at the calculated time.",
        },
        {
          tab: "Pending",
          text: "These residents are pending the completion of a Warden review.",
        },
      ],
      tooltipEligibilityText: null,
      urlSection: "wardenInPersonSecurityClassificationCommitteeReviewV2",
      zeroGrantsTooltip: null,
    },
  },
} as const satisfies ApiOpportunityConfigurationResponse;
